"use client"

import { useEffect, useSyncExternalStore } from "react"

// Module-level singleton tracking how many <SheetContent> instances are
// currently mounted. SheetContent only renders while its parent <Sheet>
// is open (base-ui portals it conditionally), so the count doubles as
// "is any sheet open right now" — useful to chrome that needs to step
// aside on mobile (bottom-pill nav etc.).
let openCount = 0
const listeners = new Set<() => void>()

export function pushSheetOpen() {
  openCount += 1
  listeners.forEach((fn) => fn())
}

export function popSheetOpen() {
  openCount = Math.max(0, openCount - 1)
  listeners.forEach((fn) => fn())
}

function subscribe(cb: () => void) {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

function getSnapshot() {
  return openCount
}

function getServerSnapshot() {
  return 0
}

export function useAnySheetOpen(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot) > 0
}

/**
 * Register a NON-Sheet overlay in the same count.
 *
 * The count is really "does something modal own the screen right now", and a
 * hand-rolled floating panel owns it just as much as a `<SheetContent>` does.
 * Anything that steps aside for sheets — the mobile bottom fade, floating pills
 * — has to step aside for those panels too, or two floating layers end up
 * stacked on each other with the taller z-index winning at random.
 *
 * Pass the live open state; the count follows it and is released on unmount.
 */
export function useOverlayOpen(open: boolean): void {
  useEffect(() => {
    if (!open) return
    pushSheetOpen()
    return () => popSheetOpen()
  }, [open])
}
